import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useParams, useLocation } from "react-router-dom";

import { Button, Divider, Grid, Typography } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";

import { DateTime } from "luxon";
import { RiderContext } from "../../context";

import { StringUtils, Parsers } from "../../util";
import { KeyValueDisplay } from "../../components/Typography";
import ReceiptLineItem from "./subcomponents/ReceiptLineItem";

const { formatNumber, dualCurrencyString, getReceiptLabel } = StringUtils;
const { filterPaymentReferenceTypes } = Parsers;

const useStyles = makeStyles((theme) => ({
  lineItemRow: {
    margin: `${theme.spacing(0.5)}px 0`,
  },
  creditButton: {
    background: theme.palette.background.buttons.strongBlue,
    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
    margin: `${theme.spacing(1)}px 0`,
    "&.Mui-disabled": {
      background: theme.palette.background.disabled,
    },
  },
}));

const GroupReceipt = () => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const location = useLocation();
  const { riderId, groupReservationId } = useParams();
  const { t } = useTranslation();
  const { activeGroupReservation, groupReceipt } = useContext(RiderContext);

  return (
    <Grid item container xs={12} spacing={1}>
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom>
          {t("Riders.GroupReservationDetail.GroupReceipt")}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h6">
          {activeGroupReservation.reservations.length
            ? DateTime.fromISO(activeGroupReservation.reservations[0].begin).toFormat(
                "MMM dd, HH:mm a",
              )
            : "--"}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <KeyValueDisplay
          label={t("Riders.GroupReservationDetail.Rate")}
          text={
            groupReceipt?.fee_type === "time"
              ? `${formatNumber(
                  groupReceipt?.rate_plan?.time_unit_fee || 0,
                  groupReceipt?.currencyLocal,
                )}/min`
              : `${formatNumber(
                  groupReceipt?.rate_plan?.distance_unit_fee || 0,
                  groupReceipt?.currencyLocal,
                )}/km`
          }
          justify="space-between"
          separator=""
          valueTypographyProps={{ variant: "h6" }}
          keyTypographyProps={{ variant: "h6" }}
        />
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <KeyValueDisplay
          label={t("Riders.GroupReservationDetail.GroupTotal")}
          text={`${formatNumber(groupReceipt?.total_amount, groupReceipt?.currencyLocal)}`}
          justify="space-between"
          separator=""
          keyTypographyProps={{ variant: "h6" }}
          valueTypographyProps={{ variant: "h6" }}
        />
      </Grid>
      {groupReceipt?.line_items?.map((lineItem, idx) => (
        <Grid item xs={12} key={lineItem.id}>
          <KeyValueDisplay
            label={`${idx === 0 ? "Host" : ""} ${t("Riders.GroupReservationDetail.Ride")} ( ${
              lineItem.vehicle_shortcode
            } )`}
            text={formatNumber(lineItem.total_amount, groupReceipt?.currencyLocal)}
            justify="space-between"
            separator=""
            className={classes.lineItemRow}
          />
        </Grid>
      ))}
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <KeyValueDisplay
          label={t("Riders.GroupReservationDetail.PaymentDetails")}
          keyTypographyProps={{ variant: "h6" }}
          separator=""
        />
      </Grid>

      {filterPaymentReferenceTypes(groupReceipt?.payments).map((payment) => (
        <ReceiptLineItem
          label={getReceiptLabel(payment, t)}
          value={formatNumber(payment.totalAmount, groupReceipt?.currencyWallet)}
          key={payment.label + payment.reference_type + payment.status}
          labelColor={payment.labelColor}
          strikeThrough={payment.strikeThrough}
        />
      ))}
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <KeyValueDisplay
          label={t("Riders.GroupReservationDetail.WalletSummary")}
          keyTypographyProps={{ variant: "h6" }}
          separator=""
        />
      </Grid>
      <ReceiptLineItem
        label={t("Riders.GroupReservationDetail.PreviousWalletBalance")}
        value={dualCurrencyString(
          groupReceipt?.exchange_rate,
          groupReceipt?.beforeWalletBalance,
          groupReceipt?.currencyWallet,
          groupReceipt?.exchange_rate?.balance?.before,
          groupReceipt?.currencyLocal,
        )}
      />
      <ReceiptLineItem
        label={t("Riders.GroupReservationDetail.NewWalletBalance")}
        value={dualCurrencyString(
          groupReceipt?.exchange_rate,
          groupReceipt?.afterWalletBalance,
          groupReceipt?.currencyWallet,
          groupReceipt?.exchange_rate?.balance?.after,
          groupReceipt?.currencyLocal,
        )}
      />
      <Grid item xs={12}>
        <Button
          disabled={!groupReceipt?.total_amount}
          component={RouterLink}
          className={classes.creditButton}
          to={{
            pathname: `/riders/${riderId}/group-reservations/${groupReservationId}/credit-wallet`,
            state: { background: location },
          }}
        >
          {t("Riders.GroupReservationDetail.Buttons.CreditWallet")}
        </Button>
      </Grid>
    </Grid>
  );
};

export default GroupReceipt;
